import { Component, EventEmitter, OnInit, OnDestroy, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatOption } from '@angular/material/core';
import { take, takeUntil } from 'rxjs/operators';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { DefaultAssumptionsService } from '../default-assumptions.service';
import { EditProductDialogComponent } from '../../plant-specifications/edit-product-dialog/edit-product-dialog.component';
import { BiogasShare } from '../../../models/project/plant-specifications/biogas-share.model';
import { DigesterType, PlantSpecifications, PlantUnitType } from '../../../models/project/plant-specifications/plant-specifications.model';
import { Product, ProductFeeType, ProductType } from '../../../models/project/plant-specifications/product.model';
import { UnitValue } from '../../../models/data-structures/unit-value.model';
import { v4 as uuid } from 'uuid';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';
import { SliderData } from '../../../models/data-structures/general/slider-data.model';

@Component({
  selector: 'app-process-input',
  templateUrl: './process-input.component.html',
  styleUrls: [
    './process-input.component.scss',
  ]
})
export class ProcessInputComponent extends UserpilotReloader implements OnInit, OnDestroy {
  @Output() pageDirty = new EventEmitter();

  onDestroyNotifier = new EventEmitter();
  trigger: string;

  plantSpec: PlantSpecifications;

  generatedProducts: Product[] = [];
  sliderData: SliderData[] = [];

  DigesterType = DigesterType;
  digesterTypes = [
    DigesterType.WET_MIXED,
    DigesterType.WET_LAGOON,
    DigesterType.DRY_CONT,
    DigesterType.DRY_BATCH
  ];

  PlantUnitType = PlantUnitType;
  plantUnits = [
    PlantUnitType.CHP,
    PlantUnitType.GENERATOR,
    PlantUnitType.BOILER,
    PlantUnitType.UPGRADER,
    PlantUnitType.DEWATERING
  ];

  plantUnitProducts: any = {};

  sliderManuallyChanged = false;
  sliderBeingInitialized = false;

  showClearSelectedDigester = false;
  showClearSelectedUnits = false;
  showClearDewateringType = false;

  constructor(
    private dialog: MatDialog,
    private projectCommService: ProjectCommunicationService,
    private assumptionsService: DefaultAssumptionsService,
    public translationService: AnessaTranslationService,
  ) {
    super();
  }

  ngOnInit() {
    this.setTranslations();
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.setTranslations();
        this.trigger = this.translationService.getCurrentLang();
      });

    this.plantSpec = this.projectCommService.project.plantSpecifications;
    this.generatedProducts = [...this.plantSpec.generatedProducts];
    this.initializeSlider();

    this.projectCommService.projectSavedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(saved => {
        if (saved) {
          // Only initialzie sliders on project save if not from manual slider change
          if (this.sliderManuallyChanged) {
            this.sliderManuallyChanged = false;
          } else {
            this.plantSpec = this.projectCommService.project.plantSpecifications;
            this.initializeSlider();
          }
        }
      });
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  setTranslations() {
    this.plantUnitProducts[PlantUnitType.CHP] = this.translationService.get('PROJECT.PLANT_SPEC.UNITS.CHP');
    this.plantUnitProducts[PlantUnitType.GENERATOR] = this.translationService.get('PROJECT.PLANT_SPEC.UNITS.GENERATOR');
    this.plantUnitProducts[PlantUnitType.BOILER] = this.translationService.get('PROJECT.PLANT_SPEC.UNITS.BOILER');
    this.plantUnitProducts[PlantUnitType.UPGRADER] = this.translationService.get('PROJECT.PLANT_SPEC.UNITS.UPGRADER');
    this.plantUnitProducts[PlantUnitType.DEWATERING] = this.translationService.get('PROJECT.PLANT_SPEC.UNITS.DEWATER');
  }

  /**
   * clears the input that is using the provided property, assumes it is
   * part of the availability variable
   * @param property: the availability variable property name that is to be cleared
   */
  clearInput(property: string) {
    const newPlantSpec = new PlantSpecifications();
    this.plantSpec[property] = newPlantSpec[property];
    this.pageDirty.emit(true);
  }

  changesMade(resetCapexOpex?: boolean) {
    this.projectCommService.setProjectPlantSpecifications(this.plantSpec, resetCapexOpex);
    this.pageDirty.emit(true);
  }

  getDropdownWidthClass() {
    if (this.hasPlantUnit(PlantUnitType.DEWATERING)) {
      return 'col-3';
    } else {
      return 'col-2';
    }
  }

  /**
   * Adds the specified product type to the plant specifications generated products array.
   * @param product the product type to add
   */
  addProduct(product: ProductType): void {
    if (product === ProductType.Electric) {
      for (const prod of this.plantSpec.generatedProducts) {
        if (prod.type === ProductType.Electric &&
            !this.generatedProducts.find(pow => pow.type === ProductType.Electric)) {
          this.generatedProducts.push(prod);
        }
      }
      if (!this.generatedProducts.find(pow => pow.type === ProductType.Electric)) {
        const elec = new Product();
        elec.name = this.translationService.get('PRODUCTS.ELECTRICITY');
        elec.type = ProductType.Electric;
        elec.priceType = ProductFeeType.INCOME;
        // Set default values
        elec.internalDemand.value = 0;
        elec.internalDemand.unit = '000000000000000000000401';
        elec.cpi.value = 0;
        elec.cpi.unit = '000000000000000000002201';
        this.generatedProducts.push(elec);
      }
    }
    if (product === ProductType.Heat) {
      for (const prod of this.plantSpec.generatedProducts) {
        if (prod.type === ProductType.Heat &&
            !this.generatedProducts.find(pow => pow.type === prod.type)) {
          this.generatedProducts.push(prod);
        }
      }
      if (!this.generatedProducts.find(pow => pow.type === ProductType.Heat)) {
        const therm = new Product();
        therm.name = this.translationService.get('PRODUCTS.THERMAL');
        therm.type = ProductType.Heat;
        therm.priceType = ProductFeeType.INCOME;
        // Set default values
        therm.internalDemand.value = 0;
        therm.internalDemand.unit = '000000000000000000000401';
        therm.cpi.value = 0;
        therm.cpi.unit = '000000000000000000002201';
        this.generatedProducts.push(therm);
      }
    }
    if (product === ProductType.Biometh) {
      for (const prod of this.plantSpec.generatedProducts) {
        if (prod.type === ProductType.Biometh &&
            !this.generatedProducts.find(pow => pow.type === prod.type)) {
          this.generatedProducts.push(prod);
        }
      }
      if (!this.generatedProducts.find(pow => pow.type === ProductType.Biometh)) {
        const gas = new Product();
        gas.name = this.translationService.get('PRODUCTS.BIOMETHANE');
        gas.type = ProductType.Biometh;
        gas.priceType = ProductFeeType.INCOME;
        // Set default values
        gas.internalDemand.value = 0;
        gas.internalDemand.unit = '000000000000000000000401';
        gas.cpi.value = 0;
        gas.cpi.unit = '000000000000000000002201';
        this.generatedProducts.push(gas);
      }
    }
    if (product === ProductType.WholeDigestate) {
      for (const prod of this.plantSpec.generatedProducts) {
        if (prod.type === ProductType.WholeDigestate &&
            !this.generatedProducts.find(pow => pow.type === prod.type)) {
          this.generatedProducts.push(prod);
        }
      }
      if (!this.generatedProducts.find(pow => pow.type === ProductType.WholeDigestate)) {
        const wdb = new Product();
        wdb.name = this.translationService.get('PRODUCTS.WDBF');
        wdb.type = ProductType.WholeDigestate;
        // Set default values
        wdb.internalDemand.value = 0;
        wdb.internalDemand.unit = '000000000000000000000401';
        wdb.cpi.value = 0;
        wdb.cpi.unit = '000000000000000000002201';
        this.generatedProducts.push(wdb);
      }
    }
    if (product === ProductType.SolidDigestate) {
      for (const prod of this.plantSpec.generatedProducts) {
        if (prod.type === ProductType.SolidDigestate &&
            !this.generatedProducts.find(pow => pow.type === prod.type)) {
          this.generatedProducts.push(prod);
        }
      }
      if (!this.generatedProducts.find(pow => pow.type === ProductType.SolidDigestate)) {
        const sdb = new Product();
        sdb.name = this.translationService.get('PRODUCTS.SDBF');
        sdb.type = ProductType.SolidDigestate;
        // Set default values
        sdb.internalDemand.value = 0;
        sdb.internalDemand.unit = '000000000000000000000401';
        sdb.cpi.value = 0;
        sdb.cpi.unit = '000000000000000000002201';
        this.generatedProducts.push(sdb);
      }
    }
    if (product === ProductType.ThinDigestate) {
      for (const prod of this.plantSpec.generatedProducts) {
        if (prod.type === ProductType.ThinDigestate &&
            !this.generatedProducts.find(pow => pow.type === prod.type)) {
          this.generatedProducts.push(prod);
        }
      }
      if (!this.generatedProducts.find(pow => pow.type === ProductType.ThinDigestate)) {
        const tdb = new Product();
        tdb.name = this.translationService.get('PRODUCTS.TDBF');
        tdb.type = ProductType.ThinDigestate;
        // Set default values
        tdb.internalDemand.value = 0;
        tdb.internalDemand.unit = '000000000000000000000401';
        tdb.cpi.value = 0;
        tdb.cpi.unit = '000000000000000000002201';
        this.generatedProducts.push(tdb);
      }
    }
    this.plantSpec.generatedProducts = this.generatedProducts;
    this.changesMade();
  }

  /**
   * Removes the specified product type from the plant specifications generated products array
   * @param product
   */
  removeProduct(product: ProductType): void {
    if (product === ProductType.Electric) {
      this.generatedProducts = this.generatedProducts.filter(prod => prod.type !== ProductType.Electric);
    } else if (product === ProductType.Heat) {
      this.generatedProducts = this.generatedProducts.filter(prod => prod.type !== ProductType.Heat);
    } else if (product === ProductType.Biometh) {
      this.generatedProducts = this.generatedProducts.filter(prod => prod.type !== ProductType.Biometh);
    } else if (product === ProductType.WholeDigestate) {
      this.generatedProducts = this.generatedProducts.filter(prod => prod.type !== ProductType.WholeDigestate);
    } else if (product === ProductType.SolidDigestate) {
      this.generatedProducts = this.generatedProducts.filter(prod => prod.type !== ProductType.SolidDigestate);
    } else if (product === ProductType.ThinDigestate) {
      this.generatedProducts = this.generatedProducts.filter(prod => prod.type !== ProductType.ThinDigestate);
    }
    this.plantSpec.generatedProducts = this.generatedProducts;
    this.changesMade();
  }

  /**
   * Updates the generated products based on the selected plant units.
   */
  updateGeneratedProducts(): void {
    for (const unit of this.plantUnits) {
      if (!this.plantSpec.selectedUnits.includes(unit)) {
        if (unit === PlantUnitType.CHP) {
          if (!this.plantSpec.selectedUnits.includes(PlantUnitType.BOILER) &&
            !this.plantSpec.selectedUnits.includes(PlantUnitType.GENERATOR)) {
            this.removeProduct(ProductType.Electric);
            this.removeProduct(ProductType.Heat);
          }
        } else if (unit === PlantUnitType.BOILER) {
          if (!this.plantSpec.selectedUnits.includes(PlantUnitType.CHP)) {
            this.removeProduct(ProductType.Heat);
          }
        } else if (unit === PlantUnitType.GENERATOR) {
          if (!this.plantSpec.selectedUnits.includes(PlantUnitType.CHP)) {
            this.removeProduct(ProductType.Electric);
          }
        } else if (unit === PlantUnitType.UPGRADER) {
          this.removeProduct(ProductType.Biometh);
        }
      } else {
        if (unit === PlantUnitType.CHP) {
          this.addProduct(ProductType.Electric);
          this.addProduct(ProductType.Heat);
        } else if (unit === PlantUnitType.BOILER) {
          if (!this.plantSpec.selectedUnits.includes(PlantUnitType.CHP)) {
            this.addProduct(ProductType.Heat);
          }
        } else if (unit === PlantUnitType.GENERATOR) {
          if (!this.plantSpec.selectedUnits.includes(PlantUnitType.CHP)) {
            this.addProduct(ProductType.Electric);
          }
        } else if (unit === PlantUnitType.UPGRADER) {
          this.addProduct(ProductType.Biometh);
        }
      }
    }
    this.updateDigestateProducts();
  }

  /**
   * Updates the generated digestate biofertilizer products based on the selected digester and dewatering unit.
   */
  updateDigestateProducts(): void {
    if (this.plantSpec.selectedDigester === DigesterType.DRY_BATCH) {
      this.removeProduct(ProductType.WholeDigestate);
      this.removeProduct(ProductType.ThinDigestate);
      this.addProduct(ProductType.SolidDigestate);
    }
    if (this.plantSpec.selectedDigester !== DigesterType.DRY_BATCH &&
      !this.plantSpec.selectedUnits.includes(PlantUnitType.DEWATERING)) {
      this.removeProduct(ProductType.SolidDigestate);
      this.removeProduct(ProductType.ThinDigestate);
      this.addProduct(ProductType.WholeDigestate);
    }
    if (this.plantSpec.selectedUnits.includes(PlantUnitType.DEWATERING)) {
      this.removeProduct(ProductType.WholeDigestate);
      this.addProduct(ProductType.SolidDigestate);
      this.addProduct(ProductType.ThinDigestate);
    }
  }

  /**
   * Updates the biogas share slider and generated products when a unit is selected or unselected.
   * @param unit the plant unit clicked
   * @param opt the MatOption clicked
   */
  updateUnits(unit: string, opt: MatOption): void {
    if (!opt.disabled) {
      // reset biogas share slider when gas distribution related units change
      if (unit === PlantUnitType.CHP || unit === PlantUnitType.GENERATOR || unit === PlantUnitType.BOILER || unit === PlantUnitType.UPGRADER) {
        this.sliderData = [];
        for (const unit of this.plantSpec.selectedUnits) {
          if (unit === PlantUnitType.CHP || unit === PlantUnitType.GENERATOR || unit === PlantUnitType.BOILER || unit === PlantUnitType.UPGRADER) {
            this.sliderData.push({name: unit, type: unit, value: 100 / this.plantSpec.selectedUnits.filter(unit => unit !== PlantUnitType.DEWATERING).length, uuid: uuid()});
          }
        }
        this.sliderData = [...this.sliderData];
        this.sliderChange();
      }

      this.assumptionsService.setPlantUnitsAssumptions(this.plantSpec, true);
      this.updateGeneratedProducts();
    }
  }

  updateDewateringAssumptions() {
    this.assumptionsService.setPlantUnitsAssumptions(this.plantSpec, true);
    this.changesMade();
  }

  /**
   * Checks if there are multiple biogas consuming units.
   */
  hasMultipleGasUnits(): boolean {
    let unitCount = 0;
    for (const unit of this.plantSpec.selectedUnits) {
      if (
        unit === PlantUnitType.CHP
        || unit === PlantUnitType.GENERATOR
        || unit === PlantUnitType.BOILER
        || unit === PlantUnitType.UPGRADER
      ) {
        unitCount++;
      }
    }
    return unitCount > 1;
  }

  /**
   * Update the value of biogasShareData when the slider value changes.
   */
  sliderChange(): void {
    this.sliderManuallyChanged = true;
    this.plantSpec.biogasShareData = [];
    for (const data of this.sliderData) {
      const shareData = new BiogasShare({
        name: data.name,
        share: new UnitValue({value: data.value, unit: '000000000000000000000401'}),
        uuid: data.uuid
      });
      this.plantSpec.biogasShareData.push(shareData);
    }
    // Only save if slider was manually moved
    if (this.sliderBeingInitialized) {
      this.sliderBeingInitialized = false;
      this.sliderManuallyChanged = false;
      return;
    } else {
      this.changesMade(false);
    }
  }

  initializeSlider() {
    this.sliderBeingInitialized = true;
    this.sliderData = [];
    for (const biogasShare of this.plantSpec.biogasShareData) {
      this.sliderData.push({name: biogasShare.name, type: biogasShare.name, value: biogasShare.share.value, uuid: biogasShare.uuid});
    }
    this.sliderData = [...this.sliderData];
  }

  /**
   * Update the plant units and generated products when the digester changes.
   */
  onDigesterChange(): void {
    if (this.plantSpec.selectedDigester === DigesterType.DRY_BATCH) {
      this.plantSpec.periodType = 'hrt';
      if (this.plantSpec.selectedUnits.includes(PlantUnitType.DEWATERING)) {
        this.plantSpec.selectedUnits.splice(this.plantSpec.selectedUnits.indexOf(PlantUnitType.DEWATERING), 1);
        this.plantSpec.selectedUnits = [...this.plantSpec.selectedUnits];
      }
    }
    this.assumptionsService.setDigesterAssumptions(this.plantSpec, this.projectCommService.getFeedstocks(), true);
    this.updateDigestateProducts();
  }

  /**
   * Checks if the unit is currently selected.
   * @param unit the plant unit to check
   */
  hasPlantUnit(unit: string): boolean {
    if (this.plantSpec.selectedUnits.includes(unit)) {
      return true;
    }
    return false;
  }

  /**
   * Opens a dialog which allows the user to edit the data of the selected product.
   * @param index the index of the product to edit.
   */
  editProductClick(index: number): void {
    const dialogRef = this.dialog.open(EditProductDialogComponent, {
      maxHeight: '90vh',
      maxWidth: '80vw',
      minHeight: '300px',
      minWidth: '800px',
      data: {
        product: Product.deepCopy(this.generatedProducts[index]),
        products: this.generatedProducts,
        isEdit: true,
        digester: this.plantSpec.selectedDigester,
        hasDewatering: this.plantSpec.selectedUnits.includes(PlantUnitType.DEWATERING)
      },
      disableClose: true,
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe((result: Product) => {
        if (result != null) {
          this.generatedProducts[index] = result;
          this.generatedProducts = [...this.generatedProducts];
          this.plantSpec.generatedProducts = this.generatedProducts;
          this.changesMade();
        }
      });
  }

  /**
   * Removes the selected product.
   * @param index the index of the product to remove.
   */
  removeProductClick(index: number): void {
    this.generatedProducts.splice(index, 1);
    this.generatedProducts = [...this.generatedProducts];
    this.plantSpec.generatedProducts = this.generatedProducts;
    this.changesMade();
  }

  /**
   * Generates and returns a custom string to display (for the selected units select).
   */
  getUnitsDisplay(): string {
    let list = '';
    for (let i = 0; i < this.plantSpec.selectedUnits.length; i++) {
      if (i !== this.plantSpec.selectedUnits.length - 1) {
        list += this.getPlantUnitDisplayName(this.plantSpec.selectedUnits[i]) + ', ';
      } else {
        list += this.getPlantUnitDisplayName(this.plantSpec.selectedUnits[i]);
      }
    }
    return list;
  }

  private getPlantUnitDisplayName(unit) {
    switch (unit) {
      case PlantUnitType.BOILER:
        return this.translationService.get('PROJECT.PLANT_SPEC.BOILER');
      case PlantUnitType.CHP:
        return this.translationService.get('PROJECT.PLANT_SPEC.CHP');
      case PlantUnitType.DEWATERING:
        return this.translationService.get('PROJECT.PLANT_SPEC.DEWATERING');
      case PlantUnitType.GENERATOR:
        return this.translationService.get('PROJECT.PLANT_SPEC.GENERATOR');
      case PlantUnitType.UPGRADER:
        return this.translationService.get('PROJECT.PLANT_SPEC.UPGRADER');
      default:
        return unit;
    }
  }

  /**
   * Checks whether the unit can be selected based in the digester type and other currently selected units.
   * @param unit the unit to check
   */
  canSelectUnit(unit: string): boolean {
    switch (unit) {
      case PlantUnitType.CHP:
        if (this.plantSpec.selectedUnits.includes(PlantUnitType.GENERATOR) || this.plantSpec.selectedUnits.includes(PlantUnitType.BOILER)) {
          return false;
        }
        break;
      case PlantUnitType.GENERATOR:
      case PlantUnitType.BOILER:
        if (this.plantSpec.selectedUnits.includes(PlantUnitType.CHP)) {
          return false;
        }
        break;
      case PlantUnitType.UPGRADER:
        break;
      case PlantUnitType.DEWATERING:
        if (this.plantSpec.selectedDigester && this.plantSpec.selectedDigester === DigesterType.DRY_BATCH) {
          return false;
        }
        break;
    }
    return true;
  }
}
